import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  ArrowLeft, Save, Plus, Trash2, Move, Type, CheckSquare, 
  Calendar, FileSignature, Hash, Mail, Phone, MapPin,
  GripVertical, Settings2, Eye, Download
} from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { type: 'text', label: 'Text Field', icon: Type, defaultWidth: 200, defaultHeight: 30 },
  { type: 'textarea', label: 'Text Area', icon: Type, defaultWidth: 300, defaultHeight: 80 },
  { type: 'checkbox', label: 'Checkbox', icon: CheckSquare, defaultWidth: 20, defaultHeight: 20 },
  { type: 'date', label: 'Date Field', icon: Calendar, defaultWidth: 150, defaultHeight: 30 },
  { type: 'signature', label: 'Signature', icon: FileSignature, defaultWidth: 250, defaultHeight: 60 },
  { type: 'number', label: 'Number', icon: Hash, defaultWidth: 120, defaultHeight: 30 },
  { type: 'email', label: 'Email', icon: Mail, defaultWidth: 200, defaultHeight: 30 },
  { type: 'phone', label: 'Phone', icon: Phone, defaultWidth: 150, defaultHeight: 30 },
  { type: 'ssn', label: 'SSN', icon: Hash, defaultWidth: 150, defaultHeight: 30 },
  { type: 'address', label: 'Address', icon: MapPin, defaultWidth: 300, defaultHeight: 30 },
  { type: 'label', label: 'Static Label', icon: Type, defaultWidth: 200, defaultHeight: 25 },
];

const AdminPdfCreator = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  // Form metadata
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState('other');
  const [formState, setFormState] = useState('');
  
  // Canvas fields
  const [fields, setFields] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [draggedFieldType, setDraggedFieldType] = useState(null);
  
  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Resizing state
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  
  // Field editor
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [editingField, setEditingField] = useState(null);
  
  // Saving
  const [saving, setSaving] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Page dimensions (Letter size at 72 DPI)
  const PAGE_WIDTH = 612;
  const PAGE_HEIGHT = 792;
  const SCALE = 0.85; // Scale for display

  // Generate unique ID
  const generateId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Handle drag start from toolbar
  const handleToolbarDragStart = (e, fieldType) => {
    setDraggedFieldType(fieldType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle drop on canvas
  const handleCanvasDrop = (e) => {
    e.preventDefault();
    if (!draggedFieldType) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / SCALE;
    const y = (e.clientY - rect.top) / SCALE;

    const fieldConfig = FIELD_TYPES.find(f => f.type === draggedFieldType);
    
    const newField = {
      id: generateId(),
      fieldId: `field_${fields.length + 1}`,
      type: draggedFieldType,
      label: fieldConfig.label,
      x: Math.max(10, Math.min(x - fieldConfig.defaultWidth / 2, PAGE_WIDTH - fieldConfig.defaultWidth - 10)),
      y: Math.max(10, Math.min(y - fieldConfig.defaultHeight / 2, PAGE_HEIGHT - fieldConfig.defaultHeight - 10)),
      width: fieldConfig.defaultWidth,
      height: fieldConfig.defaultHeight,
      required: false,
      placeholder: '',
      staticText: fieldConfig.type === 'label' ? 'Label Text' : '',
    };

    setFields([...fields, newField]);
    setSelectedFieldId(newField.id);
    setDraggedFieldType(null);
  };

  // Handle field mouse down (for dragging)
  const handleFieldMouseDown = (e, field) => {
    e.stopPropagation();
    
    // Check if clicking on resize handle
    const target = e.target;
    if (target.classList.contains('resize-handle')) {
      setIsResizing(true);
      setResizeHandle(target.dataset.handle);
      setSelectedFieldId(field.id);
      return;
    }

    setSelectedFieldId(field.id);
    setIsDragging(true);
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    setDragOffset({
      x: (e.clientX - rect.left) / SCALE - field.x,
      y: (e.clientY - rect.top) / SCALE - field.y
    });
  };

  // Handle mouse move (dragging/resizing)
  const handleMouseMove = useCallback((e) => {
    if (!isDragging && !isResizing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mouseX = (e.clientX - rect.left) / SCALE;
    const mouseY = (e.clientY - rect.top) / SCALE;

    setFields(prevFields => prevFields.map(field => {
      if (field.id !== selectedFieldId) return field;

      if (isDragging) {
        return {
          ...field,
          x: Math.max(0, Math.min(mouseX - dragOffset.x, PAGE_WIDTH - field.width)),
          y: Math.max(0, Math.min(mouseY - dragOffset.y, PAGE_HEIGHT - field.height))
        };
      }

      if (isResizing) {
        let newWidth = field.width;
        let newHeight = field.height;
        let newX = field.x;
        let newY = field.y;

        if (resizeHandle.includes('e')) {
          newWidth = Math.max(30, mouseX - field.x);
        }
        if (resizeHandle.includes('w')) {
          const delta = field.x - mouseX;
          newWidth = Math.max(30, field.width + delta);
          newX = mouseX;
        }
        if (resizeHandle.includes('s')) {
          newHeight = Math.max(20, mouseY - field.y);
        }
        if (resizeHandle.includes('n')) {
          const delta = field.y - mouseY;
          newHeight = Math.max(20, field.height + delta);
          newY = mouseY;
        }

        return { ...field, x: newX, y: newY, width: newWidth, height: newHeight };
      }

      return field;
    }));
  }, [isDragging, isResizing, selectedFieldId, dragOffset, resizeHandle]);

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  // Delete selected field
  const deleteField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    setSelectedFieldId(null);
  };

  // Open field editor
  const openFieldEditor = (field) => {
    setEditingField({ ...field });
    setShowFieldEditor(true);
  };

  // Save field changes
  const saveFieldChanges = () => {
    if (!editingField) return;
    setFields(fields.map(f => f.id === editingField.id ? editingField : f));
    setShowFieldEditor(false);
    setEditingField(null);
  };

  // Save form
  const handleSaveForm = async () => {
    if (!formName.trim()) {
      toast.error('Please enter a form name');
      return;
    }

    if (fields.length === 0) {
      toast.error('Please add at least one field to the form');
      return;
    }

    setSaving(true);
    try {
      // Prepare fields for backend
      const processedFields = fields.map((field, index) => ({
        fieldId: field.fieldId || `field_${index + 1}`,
        fieldType: field.type,
        label: field.label,
        x: Math.round(field.x),
        y: Math.round(field.y),
        width: Math.round(field.width),
        height: Math.round(field.height),
        page: 1,
        required: field.required,
        placeholder: field.placeholder || '',
        staticText: field.staticText || '',
        isVirtual: true, // All created fields are virtual
        pageWidth: PAGE_WIDTH,
        pageHeight: PAGE_HEIGHT,
      }));

      const response = await axiosInstance.post('/admin/custom-forms/create', {
        formName: formName.trim(),
        category: formCategory,
        state: formState,
        fields: processedFields,
        pageWidth: PAGE_WIDTH,
        pageHeight: PAGE_HEIGHT,
        isCreatedForm: true,
      });

      toast.success('Form created successfully!');
      setShowSaveDialog(false);
      
      // Navigate to the forms list or the mapper
      navigate('/admin/custom-forms');
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const selectedField = fields.find(f => f.id === selectedFieldId);

  // Don't process mouse events when dialogs are open
  const handleMouseMoveWrapper = (e) => {
    if (showSaveDialog || showFieldEditor) return;
    handleMouseMove(e);
  };

  const handleMouseUpWrapper = () => {
    if (showSaveDialog || showFieldEditor) return;
    handleMouseUp();
  };

  return (
    <div 
      className="min-h-screen bg-gray-100"
      onMouseMove={handleMouseMoveWrapper}
      onMouseUp={handleMouseUpWrapper}
      onMouseLeave={handleMouseUpWrapper}
    >
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/custom-forms')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">PDF Form Creator</h1>
              <p className="text-sm text-gray-500">Drag and drop fields to create your form</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-sm">
              {fields.length} fields
            </Badge>
            <Button 
              onClick={() => setShowSaveDialog(true)}
              className="bg-green-600 hover:bg-green-700"
              disabled={fields.length === 0}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Form
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="flex gap-6">
          {/* Left Sidebar - Field Palette */}
          <div className="w-64 flex-shrink-0">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Form Fields</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {FIELD_TYPES.map((fieldType) => {
                  const Icon = fieldType.icon;
                  return (
                    <div
                      key={fieldType.type}
                      draggable
                      onDragStart={(e) => handleToolbarDragStart(e, fieldType.type)}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-grab hover:bg-gray-100 border border-gray-200 transition-colors"
                    >
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <Icon className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium">{fieldType.label}</span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Selected Field Properties */}
            {selectedField && (
              <Card className="mt-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    Field Properties
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => openFieldEditor(selectedField)}
                    >
                      <Settings2 className="w-4 h-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs">Field ID</Label>
                    <Input 
                      value={selectedField.fieldId}
                      onChange={(e) => setFields(fields.map(f => 
                        f.id === selectedField.id ? {...f, fieldId: e.target.value} : f
                      ))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Label</Label>
                    <Input 
                      value={selectedField.label}
                      onChange={(e) => setFields(fields.map(f => 
                        f.id === selectedField.id ? {...f, label: e.target.value} : f
                      ))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox"
                      checked={selectedField.required}
                      onChange={(e) => setFields(fields.map(f => 
                        f.id === selectedField.id ? {...f, required: e.target.checked} : f
                      ))}
                      className="rounded"
                    />
                    <Label className="text-xs">Required Field</Label>
                  </div>
                  <div className="pt-2 border-t">
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => deleteField(selectedField.id)}
                      className="w-full"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Field
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Canvas */}
          <div className="flex-1">
            <Card className="overflow-hidden">
              <CardContent className="p-4 bg-gray-200">
                <div 
                  ref={canvasRef}
                  className="relative bg-white shadow-lg mx-auto"
                  style={{
                    width: PAGE_WIDTH * SCALE,
                    height: PAGE_HEIGHT * SCALE,
                    transform: `scale(1)`,
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleCanvasDrop}
                  onClick={() => setSelectedFieldId(null)}
                >
                  {/* Grid lines for guidance */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: 'linear-gradient(to right, #f0f0f0 1px, transparent 1px), linear-gradient(to bottom, #f0f0f0 1px, transparent 1px)',
                      backgroundSize: `${20 * SCALE}px ${20 * SCALE}px`
                    }}
                  />

                  {/* Rendered Fields */}
                  {fields.map((field) => {
                    const isSelected = field.id === selectedFieldId;
                    const fieldConfig = FIELD_TYPES.find(f => f.type === field.type);
                    const Icon = fieldConfig?.icon || Type;

                    return (
                      <div
                        key={field.id}
                        className={`absolute cursor-move group ${isSelected ? 'ring-2 ring-blue-500 z-10' : 'hover:ring-2 hover:ring-blue-300'}`}
                        style={{
                          left: field.x * SCALE,
                          top: field.y * SCALE,
                          width: field.width * SCALE,
                          height: field.height * SCALE,
                        }}
                        onMouseDown={(e) => handleFieldMouseDown(e, field)}
                        onDoubleClick={() => openFieldEditor(field)}
                      >
                        {/* Field Content */}
                        <div className={`w-full h-full border-2 rounded flex items-center px-2 ${
                          field.type === 'checkbox' ? 'border-gray-400 bg-white' :
                          field.type === 'signature' ? 'border-dashed border-gray-400 bg-gray-50' :
                          field.type === 'label' ? 'border-transparent bg-transparent' :
                          'border-gray-300 bg-white'
                        }`}>
                          {field.type === 'label' ? (
                            <span className="text-sm font-medium text-gray-700 truncate">
                              {field.staticText || field.label}
                            </span>
                          ) : field.type === 'checkbox' ? (
                            <div className="w-full h-full flex items-center justify-center">
                              <CheckSquare className="w-4 h-4 text-gray-400" />
                            </div>
                          ) : field.type === 'signature' ? (
                            <span className="text-xs text-gray-400 italic">Signature here</span>
                          ) : (
                            <span className="text-xs text-gray-400 truncate">
                              {field.placeholder || field.label}
                            </span>
                          )}
                        </div>

                        {/* Field Label (above) */}
                        {field.type !== 'label' && field.type !== 'checkbox' && (
                          <div 
                            className="absolute -top-5 left-0 text-xs font-medium text-gray-600 truncate"
                            style={{ maxWidth: field.width * SCALE }}
                          >
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                          </div>
                        )}

                        {/* Resize Handles (when selected) */}
                        {isSelected && (
                          <>
                            <div className="resize-handle absolute -right-1 -bottom-1 w-3 h-3 bg-blue-500 cursor-se-resize" data-handle="se" />
                            <div className="resize-handle absolute -left-1 -bottom-1 w-3 h-3 bg-blue-500 cursor-sw-resize" data-handle="sw" />
                            <div className="resize-handle absolute -right-1 -top-1 w-3 h-3 bg-blue-500 cursor-ne-resize" data-handle="ne" />
                            <div className="resize-handle absolute -left-1 -top-1 w-3 h-3 bg-blue-500 cursor-nw-resize" data-handle="nw" />
                            <div className="resize-handle absolute right-1/2 -bottom-1 w-3 h-3 bg-blue-500 cursor-s-resize" data-handle="s" />
                            <div className="resize-handle absolute right-1/2 -top-1 w-3 h-3 bg-blue-500 cursor-n-resize" data-handle="n" />
                            <div className="resize-handle absolute -right-1 top-1/2 w-3 h-3 bg-blue-500 cursor-e-resize" data-handle="e" />
                            <div className="resize-handle absolute -left-1 top-1/2 w-3 h-3 bg-blue-500 cursor-w-resize" data-handle="w" />
                          </>
                        )}
                      </div>
                    );
                  })}

                  {/* Empty state */}
                  {fields.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                      <div className="text-center">
                        <Plus className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p className="text-lg font-medium">Drag fields here to start building your form</p>
                        <p className="text-sm">Use the field palette on the left</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Form</DialogTitle>
            <DialogDescription>
              Enter the form details to save it for workers to fill out
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Form Name *</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., New Hire Information Form"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Category</Label>
              <select
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value)}
                className="w-full border rounded px-3 py-2 mt-1"
              >
                <option value="tax">Tax Forms</option>
                <option value="employment">Employment</option>
                <option value="safety">Safety</option>
                <option value="benefits">Benefits</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <Label>State (optional)</Label>
              <Input
                value={formState}
                onChange={(e) => setFormState(e.target.value)}
                placeholder="e.g., Ohio, California"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveForm} disabled={saving}>
              {saving ? 'Saving...' : 'Save Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Field Editor Dialog */}
      <Dialog open={showFieldEditor} onOpenChange={setShowFieldEditor}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Field</DialogTitle>
          </DialogHeader>

          {editingField && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Field ID</Label>
                <Input
                  value={editingField.fieldId}
                  onChange={(e) => setEditingField({...editingField, fieldId: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Label</Label>
                <Input
                  value={editingField.label}
                  onChange={(e) => setEditingField({...editingField, label: e.target.value})}
                  className="mt-1"
                />
              </div>

              {editingField.type === 'label' && (
                <div>
                  <Label>Static Text</Label>
                  <Input
                    value={editingField.staticText}
                    onChange={(e) => setEditingField({...editingField, staticText: e.target.value})}
                    className="mt-1"
                  />
                </div>
              )}

              {editingField.type !== 'label' && editingField.type !== 'checkbox' && (
                <div>
                  <Label>Placeholder</Label>
                  <Input
                    value={editingField.placeholder}
                    onChange={(e) => setEditingField({...editingField, placeholder: e.target.value})}
                    className="mt-1"
                  />
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingField.required}
                  onChange={(e) => setEditingField({...editingField, required: e.target.checked})}
                  className="rounded"
                />
                <Label>Required Field</Label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Width</Label>
                  <Input
                    type="number"
                    value={Math.round(editingField.width)}
                    onChange={(e) => setEditingField({...editingField, width: parseInt(e.target.value) || 50})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Height</Label>
                  <Input
                    type="number"
                    value={Math.round(editingField.height)}
                    onChange={(e) => setEditingField({...editingField, height: parseInt(e.target.value) || 20})}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFieldEditor(false)}>
              Cancel
            </Button>
            <Button onClick={saveFieldChanges}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPdfCreator;
